import * as z from 'zod';

export const CreateJoinApplicationRequest = /*#__PURE__*/ z.object({
  joinApplication: z
    .object({
      _id: z
        .string()
        .describe('JoinApplication ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time a supported lifecycle, payment, or tag action changes the JoinApplication.\n\nThis API does not provide a generic update operation. The revision is read-only and is not sent when creating or changing a JoinApplication.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the join application was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the join application was last updated.')
        .optional()
        .nullable(),
      memberId: z
        .string()
        .describe(
          "Member ID of the site member applying or invited to participate in the program.\n\nRequired when creating applications as a site owner with `manage_applications` permission.\nAutomatically set to the caller's member ID when members create their own applications."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      programId: z
        .string()
        .describe('Program ID which this join application is related to.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        ),
      status: z
        .enum([
          'INVITED',
          'APPROVAL_PENDING',
          'APPROVED',
          'CANCELLED',
          'DECLINED',
          'SUSPENDED',
        ])
        .optional(),
      paymentStatus: z
        .enum([
          'NO_PAYMENT_REQUIRED',
          'PAYMENT_PENDING',
          'PAYMENT_IN_PROGRESS',
          'PENDING_OWNER_APPROVAL',
          'PAYMENT_SUCCESSFUL',
          'PAYMENT_FAILED',
          'PAYMENT_CANCELED',
        ])
        .optional(),
      paymentInfo: z
        .object({
          paymentOrderId: z
            .string()
            .describe(
              'One-time payment fields (payment_order_id, offline_transaction_id, and coupon_id) can be populated together as applicable. They are mutually exclusive with paid_plan_ids, which is populated only for Pricing Plans payments.\nWix Payments order id, populated when UoU selects One-time payment at the checkout'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          offlineTransactionId: z
            .string()
            .describe(
              'Offline transaction id, populated when UoU selects manual payment type at the checkout'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          paidPlanIds: z.array(z.string()).max(100).optional(),
          couponId: z
            .string()
            .describe(
              'Coupon ID applied to the payment order, populated when a coupon is applied via ApplyCoupon'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Payment info - represents associated Wix Payments or Pricing Plans data.'
        )
        .optional(),
      member: z
        .object({
          firstName: z
            .string()
            .describe(
              "Read-only snapshot of the member identified by member_id. It is copied from the Members API and is not the authoritative member record.\nMember contact's first name"
            )
            .max(1000)
            .optional()
            .nullable(),
          lastName: z
            .string()
            .describe("Member contact's last name")
            .max(1000)
            .optional()
            .nullable(),
          email: z
            .string()
            .describe('Member login email')
            .email()
            .optional()
            .nullable(),
          contactId: z
            .string()
            .describe('Member contact id')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          nickname: z
            .string()
            .describe('Member nickname')
            .max(1000)
            .optional()
            .nullable(),
          profileImageUrl: z
            .string()
            .describe('Member profile image url')
            .url()
            .optional()
            .nullable(),
        })
        .describe(
          'Read-only snapshot of member information from the Members API for `member_id`.\nThis is not the authoritative member record.'
        )
        .optional(),
      participantId: z
        .string()
        .describe(
          'Resulting participant (on APPROVED & NO_PAYMENT_REQUIRED/PAYMENT_SUCCESSFUL).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the JoinApplication.\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields)\nmust be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, typically restricted from site members and visitors.'
            )
            .optional(),
          publicTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone with access to the entity, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags for categorizing and organizing join applications.')
        .optional(),
    })
    .describe('JoinApplication to be created.'),
});
export const CreateJoinApplicationResponse = /*#__PURE__*/ z.object({
  _id: z
    .string()
    .describe('JoinApplication ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time a supported lifecycle, payment, or tag action changes the JoinApplication.\n\nThis API does not provide a generic update operation. The revision is read-only and is not sent when creating or changing a JoinApplication.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the join application was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the join application was last updated.')
    .optional()
    .nullable(),
  memberId: z
    .string()
    .describe(
      "Member ID of the site member applying or invited to participate in the program.\n\nRequired when creating applications as a site owner with `manage_applications` permission.\nAutomatically set to the caller's member ID when members create their own applications."
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  programId: z
    .string()
    .describe('Program ID which this join application is related to.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  status: z
    .enum([
      'INVITED',
      'APPROVAL_PENDING',
      'APPROVED',
      'CANCELLED',
      'DECLINED',
      'SUSPENDED',
    ])
    .describe(
      'Current status of the join application.\n\nRead-only for member-initiated applications.\nSite owners with `manage_applications` permission can set an initial status when creating applications on behalf of members.'
    )
    .optional(),
  paymentStatus: z
    .enum([
      'NO_PAYMENT_REQUIRED',
      'PAYMENT_PENDING',
      'PAYMENT_IN_PROGRESS',
      'PENDING_OWNER_APPROVAL',
      'PAYMENT_SUCCESSFUL',
      'PAYMENT_FAILED',
      'PAYMENT_CANCELED',
    ])
    .describe('Payment status of join application.')
    .optional(),
  paymentInfo: z
    .object({
      paymentOrderId: z
        .string()
        .describe(
          'One-time payment fields (payment_order_id, offline_transaction_id, and coupon_id) can be populated together as applicable. They are mutually exclusive with paid_plan_ids, which is populated only for Pricing Plans payments.\nWix Payments order id, populated when UoU selects One-time payment at the checkout'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      offlineTransactionId: z
        .string()
        .describe(
          'Offline transaction id, populated when UoU selects manual payment type at the checkout'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      paidPlanIds: z.array(z.string()).max(100).optional(),
      couponId: z
        .string()
        .describe(
          'Coupon ID applied to the payment order, populated when a coupon is applied via ApplyCoupon'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
    })
    .describe(
      'Payment info - represents associated Wix Payments or Pricing Plans data.'
    )
    .optional(),
  member: z
    .object({
      firstName: z
        .string()
        .describe(
          "Read-only snapshot of the member identified by member_id. It is copied from the Members API and is not the authoritative member record.\nMember contact's first name"
        )
        .max(1000)
        .optional()
        .nullable(),
      lastName: z
        .string()
        .describe("Member contact's last name")
        .max(1000)
        .optional()
        .nullable(),
      email: z
        .string()
        .describe('Member login email')
        .email()
        .optional()
        .nullable(),
      contactId: z
        .string()
        .describe('Member contact id')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      nickname: z
        .string()
        .describe('Member nickname')
        .max(1000)
        .optional()
        .nullable(),
      profileImageUrl: z
        .string()
        .describe('Member profile image url')
        .url()
        .optional()
        .nullable(),
    })
    .describe(
      'Read-only snapshot of member information from the Members API for `member_id`.\nThis is not the authoritative member record.'
    )
    .optional(),
  participantId: z
    .string()
    .describe(
      'Resulting participant (on APPROVED & NO_PAYMENT_REQUIRED/PAYMENT_SUCCESSFUL).'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Custom field data for the JoinApplication.\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields)\nmust be configured in the app dashboard before they can be accessed with API calls.'
    )
    .optional(),
  tags: z
    .object({
      privateTags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that require an additional permission in order to access them, typically restricted from site members and visitors.'
        )
        .optional(),
      publicTags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that are exposed to anyone with access to the entity, including site members and visitors.'
        )
        .optional(),
    })
    .describe('Tags for categorizing and organizing join applications.')
    .optional(),
});
export const GetJoinApplicationRequest = /*#__PURE__*/ z.object({
  joinApplicationId: z
    .string()
    .describe('ID of the JoinApplication to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetJoinApplicationResponse = /*#__PURE__*/ z.object({
  _id: z
    .string()
    .describe('JoinApplication ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time a supported lifecycle, payment, or tag action changes the JoinApplication.\n\nThis API does not provide a generic update operation. The revision is read-only and is not sent when creating or changing a JoinApplication.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the join application was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the join application was last updated.')
    .optional()
    .nullable(),
  memberId: z
    .string()
    .describe(
      "Member ID of the site member applying or invited to participate in the program.\n\nRequired when creating applications as a site owner with `manage_applications` permission.\nAutomatically set to the caller's member ID when members create their own applications."
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  programId: z
    .string()
    .describe('Program ID which this join application is related to.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  status: z
    .enum([
      'INVITED',
      'APPROVAL_PENDING',
      'APPROVED',
      'CANCELLED',
      'DECLINED',
      'SUSPENDED',
    ])
    .describe(
      'Current status of the join application.\n\nRead-only for member-initiated applications.\nSite owners with `manage_applications` permission can set an initial status when creating applications on behalf of members.'
    )
    .optional(),
  paymentStatus: z
    .enum([
      'NO_PAYMENT_REQUIRED',
      'PAYMENT_PENDING',
      'PAYMENT_IN_PROGRESS',
      'PENDING_OWNER_APPROVAL',
      'PAYMENT_SUCCESSFUL',
      'PAYMENT_FAILED',
      'PAYMENT_CANCELED',
    ])
    .describe('Payment status of join application.')
    .optional(),
  paymentInfo: z
    .object({
      paymentOrderId: z
        .string()
        .describe(
          'One-time payment fields (payment_order_id, offline_transaction_id, and coupon_id) can be populated together as applicable. They are mutually exclusive with paid_plan_ids, which is populated only for Pricing Plans payments.\nWix Payments order id, populated when UoU selects One-time payment at the checkout'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      offlineTransactionId: z
        .string()
        .describe(
          'Offline transaction id, populated when UoU selects manual payment type at the checkout'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      paidPlanIds: z.array(z.string()).max(100).optional(),
      couponId: z
        .string()
        .describe(
          'Coupon ID applied to the payment order, populated when a coupon is applied via ApplyCoupon'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
    })
    .describe(
      'Payment info - represents associated Wix Payments or Pricing Plans data.'
    )
    .optional(),
  member: z
    .object({
      firstName: z
        .string()
        .describe(
          "Read-only snapshot of the member identified by member_id. It is copied from the Members API and is not the authoritative member record.\nMember contact's first name"
        )
        .max(1000)
        .optional()
        .nullable(),
      lastName: z
        .string()
        .describe("Member contact's last name")
        .max(1000)
        .optional()
        .nullable(),
      email: z
        .string()
        .describe('Member login email')
        .email()
        .optional()
        .nullable(),
      contactId: z
        .string()
        .describe('Member contact id')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      nickname: z
        .string()
        .describe('Member nickname')
        .max(1000)
        .optional()
        .nullable(),
      profileImageUrl: z
        .string()
        .describe('Member profile image url')
        .url()
        .optional()
        .nullable(),
    })
    .describe(
      'Read-only snapshot of member information from the Members API for `member_id`.\nThis is not the authoritative member record.'
    )
    .optional(),
  participantId: z
    .string()
    .describe(
      'Resulting participant (on APPROVED & NO_PAYMENT_REQUIRED/PAYMENT_SUCCESSFUL).'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Custom field data for the JoinApplication.\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields)\nmust be configured in the app dashboard before they can be accessed with API calls.'
    )
    .optional(),
  tags: z
    .object({
      privateTags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that require an additional permission in order to access them, typically restricted from site members and visitors.'
        )
        .optional(),
      publicTags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that are exposed to anyone with access to the entity, including site members and visitors.'
        )
        .optional(),
    })
    .describe('Tags for categorizing and organizing join applications.')
    .optional(),
});
export const DeleteJoinApplicationRequest = /*#__PURE__*/ z.object({
  joinApplicationId: z
    .string()
    .describe('Id of the JoinApplication to delete.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteJoinApplicationResponse = /*#__PURE__*/ z.object({});
export const QueryJoinApplicationsRequest = /*#__PURE__*/ z.object({
  query: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object.\n\nLearn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .max(100)
          .optional(),
      }),
      z.xor([
        z.object({
          paging: z.never().optional(),
          cursorPaging: z.never().optional(),
        }),
        z.object({
          cursorPaging: z.never().optional(),
          paging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Number of items to load.')
                .min(0)
                .optional()
                .nullable(),
              offset: z
                .number()
                .int()
                .describe('Number of items to skip in the current sort order.')
                .min(0)
                .optional()
                .nullable(),
            })
            .describe(
              'Paging options to limit and offset the number of items.'
            ),
        }),
        z.object({
          paging: z.never().optional(),
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Maximum number of items to return in the results.')
                .min(0)
                .max(100)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.'
            ),
        }),
      ])
    )
    .describe('WQL expression.'),
});
export const QueryJoinApplicationsResponse = /*#__PURE__*/ z.object({
  joinApplications: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('JoinApplication ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time a supported lifecycle, payment, or tag action changes the JoinApplication.\n\nThis API does not provide a generic update operation. The revision is read-only and is not sent when creating or changing a JoinApplication.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the join application was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the join application was last updated.')
          .optional()
          .nullable(),
        memberId: z
          .string()
          .describe(
            "Member ID of the site member applying or invited to participate in the program.\n\nRequired when creating applications as a site owner with `manage_applications` permission.\nAutomatically set to the caller's member ID when members create their own applications."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        programId: z
          .string()
          .describe('Program ID which this join application is related to.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        status: z
          .enum([
            'INVITED',
            'APPROVAL_PENDING',
            'APPROVED',
            'CANCELLED',
            'DECLINED',
            'SUSPENDED',
          ])
          .describe(
            'Current status of the join application.\n\nRead-only for member-initiated applications.\nSite owners with `manage_applications` permission can set an initial status when creating applications on behalf of members.'
          )
          .optional(),
        paymentStatus: z
          .enum([
            'NO_PAYMENT_REQUIRED',
            'PAYMENT_PENDING',
            'PAYMENT_IN_PROGRESS',
            'PENDING_OWNER_APPROVAL',
            'PAYMENT_SUCCESSFUL',
            'PAYMENT_FAILED',
            'PAYMENT_CANCELED',
          ])
          .describe('Payment status of join application.')
          .optional(),
        paymentInfo: z
          .object({
            paymentOrderId: z
              .string()
              .describe(
                'One-time payment fields (payment_order_id, offline_transaction_id, and coupon_id) can be populated together as applicable. They are mutually exclusive with paid_plan_ids, which is populated only for Pricing Plans payments.\nWix Payments order id, populated when UoU selects One-time payment at the checkout'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            offlineTransactionId: z
              .string()
              .describe(
                'Offline transaction id, populated when UoU selects manual payment type at the checkout'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            paidPlanIds: z.array(z.string()).max(100).optional(),
            couponId: z
              .string()
              .describe(
                'Coupon ID applied to the payment order, populated when a coupon is applied via ApplyCoupon'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
          })
          .describe(
            'Payment info - represents associated Wix Payments or Pricing Plans data.'
          )
          .optional(),
        member: z
          .object({
            firstName: z
              .string()
              .describe(
                "Read-only snapshot of the member identified by member_id. It is copied from the Members API and is not the authoritative member record.\nMember contact's first name"
              )
              .max(1000)
              .optional()
              .nullable(),
            lastName: z
              .string()
              .describe("Member contact's last name")
              .max(1000)
              .optional()
              .nullable(),
            email: z
              .string()
              .describe('Member login email')
              .email()
              .optional()
              .nullable(),
            contactId: z
              .string()
              .describe('Member contact id')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            nickname: z
              .string()
              .describe('Member nickname')
              .max(1000)
              .optional()
              .nullable(),
            profileImageUrl: z
              .string()
              .describe('Member profile image url')
              .url()
              .optional()
              .nullable(),
          })
          .describe(
            'Read-only snapshot of member information from the Members API for `member_id`.\nThis is not the authoritative member record.'
          )
          .optional(),
        participantId: z
          .string()
          .describe(
            'Resulting participant (on APPROVED & NO_PAYMENT_REQUIRED/PAYMENT_SUCCESSFUL).'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for the JoinApplication.\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields)\nmust be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        tags: z
          .object({
            privateTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that require an additional permission in order to access them, typically restricted from site members and visitors.'
              )
              .optional(),
            publicTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone with access to the entity, including site members and visitors.'
              )
              .optional(),
          })
          .describe('Tags for categorizing and organizing join applications.')
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
    })
    .describe('Paging metadata')
    .optional(),
});
export const SearchJoinApplicationsRequest = /*#__PURE__*/ z.object({
  search: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object.\n\nLearn more about the [filter section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-filter-section).'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .max(10)
          .optional(),
        aggregations: z
          .array(
            z.intersection(
              z.object({
                name: z
                  .string()
                  .describe(
                    'User-defined name of aggregation, should be unique, will appear in aggregation results.'
                  )
                  .max(100)
                  .optional()
                  .nullable(),
                type: z
                  .enum([
                    'VALUE',
                    'RANGE',
                    'SCALAR',
                    'DATE_HISTOGRAM',
                    'NESTED',
                  ])
                  .optional(),
                fieldPath: z
                  .string()
                  .describe(
                    'Field to aggregate by, use dot notation to specify json path.'
                  )
                  .max(200)
                  .optional(),
              }),
              z.xor([
                z.object({
                  value: z.never().optional(),
                  range: z.never().optional(),
                  scalar: z.never().optional(),
                  dateHistogram: z.never().optional(),
                  nested: z.never().optional(),
                }),
                z.object({
                  range: z.never().optional(),
                  scalar: z.never().optional(),
                  dateHistogram: z.never().optional(),
                  nested: z.never().optional(),
                  value: z
                    .intersection(
                      z.object({
                        sortType: z
                          .enum(['COUNT', 'VALUE'])
                          .describe(
                            'Whether to sort by number of matches or value of the field.'
                          )
                          .optional(),
                        sortDirection: z
                          .enum(['DESC', 'ASC'])
                          .describe(
                            'Whether to sort in ascending or descending order.'
                          )
                          .optional(),
                        limit: z
                          .number()
                          .int()
                          .describe(
                            'How many aggregations to return. Can be between 1 and 250. 10 is the default.'
                          )
                          .optional()
                          .nullable(),
                        missingValues: z
                          .enum(['EXCLUDE', 'INCLUDE'])
                          .describe(
                            'Whether to include or exclude missing values from the aggregation results. Default: `EXCLUDE`.'
                          )
                          .optional(),
                      }),
                      z.xor([
                        z.object({ includeOptions: z.never().optional() }),
                        z.object({
                          includeOptions: z
                            .object({
                              addToBucket: z
                                .string()
                                .describe(
                                  'Specify custom bucket name. Defaults are [string -> "N/A"], [int -> "0"], [bool -> "false"] ...'
                                )
                                .max(20)
                                .optional(),
                            })
                            .describe('Options for including missing values.'),
                        }),
                      ])
                    )
                    .describe('Value aggregation.'),
                }),
                z.object({
                  value: z.never().optional(),
                  scalar: z.never().optional(),
                  dateHistogram: z.never().optional(),
                  nested: z.never().optional(),
                  range: z
                    .object({
                      buckets: z
                        .array(
                          z.object({
                            from: z
                              .number()
                              .describe(
                                'Inclusive lower bound of the range. Required if `to` is not provided.'
                              )
                              .optional()
                              .nullable(),
                            to: z
                              .number()
                              .describe(
                                'Exclusive upper bound of the range. Required if `from` is not provided.'
                              )
                              .optional()
                              .nullable(),
                          })
                        )
                        .max(50)
                        .optional(),
                    })
                    .describe('Range aggregation.'),
                }),
                z.object({
                  value: z.never().optional(),
                  range: z.never().optional(),
                  dateHistogram: z.never().optional(),
                  nested: z.never().optional(),
                  scalar: z
                    .object({
                      type: z
                        .enum(['COUNT_DISTINCT', 'MIN', 'MAX'])
                        .describe(
                          'Define the operator for the scalar aggregation.'
                        )
                        .optional(),
                    })
                    .describe('Scalar aggregation.'),
                }),
                z.object({
                  value: z.never().optional(),
                  range: z.never().optional(),
                  scalar: z.never().optional(),
                  nested: z.never().optional(),
                  dateHistogram: z
                    .object({
                      interval: z
                        .enum([
                          'YEAR',
                          'MONTH',
                          'WEEK',
                          'DAY',
                          'HOUR',
                          'MINUTE',
                          'SECOND',
                        ])
                        .describe('Interval for date histogram aggregation.')
                        .optional(),
                    })
                    .describe('Date histogram aggregation.'),
                }),
                z.object({
                  value: z.never().optional(),
                  range: z.never().optional(),
                  scalar: z.never().optional(),
                  dateHistogram: z.never().optional(),
                  nested: z
                    .object({
                      nestedAggregations: z
                        .array(
                          z.intersection(
                            z.object({
                              name: z
                                .string()
                                .describe(
                                  'User-defined name of aggregation, should be unique, will appear in aggregation results.'
                                )
                                .max(100)
                                .optional()
                                .nullable(),
                              type: z
                                .enum([
                                  'VALUE',
                                  'RANGE',
                                  'SCALAR',
                                  'DATE_HISTOGRAM',
                                ])
                                .optional(),
                              fieldPath: z
                                .string()
                                .describe(
                                  'Field to aggregate by, use dot notation to specify json path.'
                                )
                                .max(200)
                                .optional(),
                            }),
                            z.xor([
                              z.object({
                                value: z.never().optional(),
                                range: z.never().optional(),
                                scalar: z.never().optional(),
                                dateHistogram: z.never().optional(),
                              }),
                              z.object({
                                range: z.never().optional(),
                                scalar: z.never().optional(),
                                dateHistogram: z.never().optional(),
                                value: z
                                  .intersection(
                                    z.object({
                                      sortType: z
                                        .enum(['COUNT', 'VALUE'])
                                        .describe(
                                          'Whether to sort by number of matches or value of the field.'
                                        )
                                        .optional(),
                                      sortDirection: z
                                        .enum(['DESC', 'ASC'])
                                        .describe(
                                          'Whether to sort in ascending or descending order.'
                                        )
                                        .optional(),
                                      limit: z
                                        .number()
                                        .int()
                                        .describe(
                                          'How many aggregations to return. Can be between 1 and 250. 10 is the default.'
                                        )
                                        .optional()
                                        .nullable(),
                                      missingValues: z
                                        .enum(['EXCLUDE', 'INCLUDE'])
                                        .describe(
                                          'Whether to include or exclude missing values from the aggregation results. Default: `EXCLUDE`.'
                                        )
                                        .optional(),
                                    }),
                                    z.xor([
                                      z.object({
                                        includeOptions: z.never().optional(),
                                      }),
                                      z.object({
                                        includeOptions: z
                                          .object({
                                            addToBucket: z
                                              .string()
                                              .describe(
                                                'Specify custom bucket name. Defaults are [string -> "N/A"], [int -> "0"], [bool -> "false"] ...'
                                              )
                                              .max(20)
                                              .optional(),
                                          })
                                          .describe(
                                            'Options for including missing values.'
                                          ),
                                      }),
                                    ])
                                  )
                                  .describe('Value aggregation.'),
                              }),
                              z.object({
                                value: z.never().optional(),
                                scalar: z.never().optional(),
                                dateHistogram: z.never().optional(),
                                range: z
                                  .object({
                                    buckets: z
                                      .array(
                                        z.object({
                                          from: z
                                            .number()
                                            .describe(
                                              'Inclusive lower bound of the range. Required if `to` is not provided.'
                                            )
                                            .optional()
                                            .nullable(),
                                          to: z
                                            .number()
                                            .describe(
                                              'Exclusive upper bound of the range. Required if `from` is not provided.'
                                            )
                                            .optional()
                                            .nullable(),
                                        })
                                      )
                                      .max(50)
                                      .optional(),
                                  })
                                  .describe('Range aggregation.'),
                              }),
                              z.object({
                                value: z.never().optional(),
                                range: z.never().optional(),
                                dateHistogram: z.never().optional(),
                                scalar: z
                                  .object({
                                    type: z
                                      .enum(['COUNT_DISTINCT', 'MIN', 'MAX'])
                                      .describe(
                                        'Define the operator for the scalar aggregation.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Scalar aggregation.'),
                              }),
                              z.object({
                                value: z.never().optional(),
                                range: z.never().optional(),
                                scalar: z.never().optional(),
                                dateHistogram: z
                                  .object({
                                    interval: z
                                      .enum([
                                        'YEAR',
                                        'MONTH',
                                        'WEEK',
                                        'DAY',
                                        'HOUR',
                                        'MINUTE',
                                        'SECOND',
                                      ])
                                      .describe(
                                        'Interval for date histogram aggregation.'
                                      )
                                      .optional(),
                                  })
                                  .describe('Date histogram aggregation.'),
                              }),
                            ])
                          )
                        )
                        .min(2)
                        .max(3)
                        .optional(),
                    })
                    .describe('Nested aggregation.'),
                }),
              ])
            )
          )
          .max(10)
          .optional(),
        search: z
          .object({
            mode: z.enum(['OR', 'AND']).optional(),
            expression: z
              .string()
              .describe('Search term or expression.')
              .max(100)
              .optional()
              .nullable(),
            fields: z.array(z.string()).max(20).optional(),
            fuzzy: z
              .boolean()
              .describe(
                'Whether to use auto fuzzy search (allowing typos by a managed proximity algorithm).'
              )
              .optional(),
          })
          .describe('Free text to match in searchable fields.')
          .optional(),
        timeZone: z
          .string()
          .describe(
            'UTC offset or IANA time zone. Valid values are\nISO 8601 UTC offsets, such as +02:00 or -06:00,\nand IANA time zone IDs, such as Europe/Rome.\n\nAffects all filters and aggregations returned values.\nYou may override this behavior in a specific filter by providing\ntimestamps including time zone. For example, `"2023-12-20T10:52:34.795Z"`.'
          )
          .max(50)
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({ cursorPaging: z.never().optional() }),
        z.object({
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Maximum number of items to return in the results.')
                .min(0)
                .max(100)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor paging options.\n\nLearn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).'
            ),
        }),
      ])
    )
    .describe('WQL expression.'),
});
export const SearchJoinApplicationsResponse = /*#__PURE__*/ z.object({
  joinApplications: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('JoinApplication ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time a supported lifecycle, payment, or tag action changes the JoinApplication.\n\nThis API does not provide a generic update operation. The revision is read-only and is not sent when creating or changing a JoinApplication.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the join application was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the join application was last updated.')
          .optional()
          .nullable(),
        memberId: z
          .string()
          .describe(
            "Member ID of the site member applying or invited to participate in the program.\n\nRequired when creating applications as a site owner with `manage_applications` permission.\nAutomatically set to the caller's member ID when members create their own applications."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        programId: z
          .string()
          .describe('Program ID which this join application is related to.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        status: z
          .enum([
            'INVITED',
            'APPROVAL_PENDING',
            'APPROVED',
            'CANCELLED',
            'DECLINED',
            'SUSPENDED',
          ])
          .describe(
            'Current status of the join application.\n\nRead-only for member-initiated applications.\nSite owners with `manage_applications` permission can set an initial status when creating applications on behalf of members.'
          )
          .optional(),
        paymentStatus: z
          .enum([
            'NO_PAYMENT_REQUIRED',
            'PAYMENT_PENDING',
            'PAYMENT_IN_PROGRESS',
            'PENDING_OWNER_APPROVAL',
            'PAYMENT_SUCCESSFUL',
            'PAYMENT_FAILED',
            'PAYMENT_CANCELED',
          ])
          .describe('Payment status of join application.')
          .optional(),
        paymentInfo: z
          .object({
            paymentOrderId: z
              .string()
              .describe(
                'One-time payment fields (payment_order_id, offline_transaction_id, and coupon_id) can be populated together as applicable. They are mutually exclusive with paid_plan_ids, which is populated only for Pricing Plans payments.\nWix Payments order id, populated when UoU selects One-time payment at the checkout'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            offlineTransactionId: z
              .string()
              .describe(
                'Offline transaction id, populated when UoU selects manual payment type at the checkout'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            paidPlanIds: z.array(z.string()).max(100).optional(),
            couponId: z
              .string()
              .describe(
                'Coupon ID applied to the payment order, populated when a coupon is applied via ApplyCoupon'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
          })
          .describe(
            'Payment info - represents associated Wix Payments or Pricing Plans data.'
          )
          .optional(),
        member: z
          .object({
            firstName: z
              .string()
              .describe(
                "Read-only snapshot of the member identified by member_id. It is copied from the Members API and is not the authoritative member record.\nMember contact's first name"
              )
              .max(1000)
              .optional()
              .nullable(),
            lastName: z
              .string()
              .describe("Member contact's last name")
              .max(1000)
              .optional()
              .nullable(),
            email: z
              .string()
              .describe('Member login email')
              .email()
              .optional()
              .nullable(),
            contactId: z
              .string()
              .describe('Member contact id')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            nickname: z
              .string()
              .describe('Member nickname')
              .max(1000)
              .optional()
              .nullable(),
            profileImageUrl: z
              .string()
              .describe('Member profile image url')
              .url()
              .optional()
              .nullable(),
          })
          .describe(
            'Read-only snapshot of member information from the Members API for `member_id`.\nThis is not the authoritative member record.'
          )
          .optional(),
        participantId: z
          .string()
          .describe(
            'Resulting participant (on APPROVED & NO_PAYMENT_REQUIRED/PAYMENT_SUCCESSFUL).'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for the JoinApplication.\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields)\nmust be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        tags: z
          .object({
            privateTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that require an additional permission in order to access them, typically restricted from site members and visitors.'
              )
              .optional(),
            publicTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone with access to the entity, including site members and visitors.'
              )
              .optional(),
          })
          .describe('Tags for categorizing and organizing join applications.')
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in current page.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata')
    .optional(),
  aggregationData: z
    .object({
      results: z
        .array(
          z.intersection(
            z.object({
              name: z
                .string()
                .describe(
                  'User-defined name of aggregation as derived from search request.'
                )
                .max(100)
                .optional(),
              type: z
                .enum(['VALUE', 'RANGE', 'SCALAR', 'DATE_HISTOGRAM', 'NESTED'])
                .describe(
                  'Type of aggregation that must match provided kind as derived from search request.'
                )
                .optional(),
              fieldPath: z
                .string()
                .describe(
                  'Field to aggregate by as derived from search request.'
                )
                .max(200)
                .optional(),
            }),
            z.xor([
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
              }),
              z.object({
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                values: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          value: z
                            .string()
                            .describe('Value of the field.')
                            .max(100)
                            .optional(),
                          count: z
                            .number()
                            .int()
                            .describe('Count of entities with this value.')
                            .optional(),
                        })
                      )
                      .max(250)
                      .optional(),
                  })
                  .describe('Value aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                ranges: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          from: z
                            .number()
                            .describe('Inclusive lower bound of the range.')
                            .optional()
                            .nullable(),
                          to: z
                            .number()
                            .describe('Exclusive upper bound of the range.')
                            .optional()
                            .nullable(),
                          count: z
                            .number()
                            .int()
                            .describe('Count of entities in this range.')
                            .optional(),
                        })
                      )
                      .max(50)
                      .optional(),
                  })
                  .describe('Range aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                scalar: z
                  .object({
                    type: z
                      .enum(['COUNT_DISTINCT', 'MIN', 'MAX'])
                      .describe('Type of scalar aggregation.')
                      .optional(),
                    value: z
                      .number()
                      .describe('Value of the scalar aggregation.')
                      .optional(),
                  })
                  .describe('Scalar aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                groupedByValue: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          value: z
                            .string()
                            .describe('Value of the field.')
                            .max(1000)
                            .optional(),
                          nestedResults: z
                            .intersection(
                              z.object({
                                name: z
                                  .string()
                                  .describe(
                                    'User-defined name of aggregation, matches the one provided in request.'
                                  )
                                  .max(100)
                                  .optional(),
                                type: z
                                  .enum([
                                    'VALUE',
                                    'RANGE',
                                    'SCALAR',
                                    'DATE_HISTOGRAM',
                                    'NESTED',
                                  ])
                                  .describe(
                                    'Type of aggregation that matches result.'
                                  )
                                  .optional(),
                                fieldPath: z
                                  .string()
                                  .describe(
                                    'Field to aggregate by, matches the one provided in request.'
                                  )
                                  .max(200)
                                  .optional(),
                              }),
                              z.xor([
                                z.object({
                                  values: z.never().optional(),
                                  ranges: z.never().optional(),
                                  scalar: z.never().optional(),
                                }),
                                z.object({
                                  ranges: z.never().optional(),
                                  scalar: z.never().optional(),
                                  values: z
                                    .object({
                                      results: z
                                        .array(
                                          z.object({
                                            value: z
                                              .string()
                                              .describe('Value of the field.')
                                              .max(100)
                                              .optional(),
                                            count: z
                                              .number()
                                              .int()
                                              .describe(
                                                'Count of entities with this value.'
                                              )
                                              .optional(),
                                          })
                                        )
                                        .max(250)
                                        .optional(),
                                    })
                                    .describe('Value aggregation results.'),
                                }),
                                z.object({
                                  values: z.never().optional(),
                                  scalar: z.never().optional(),
                                  ranges: z
                                    .object({
                                      results: z
                                        .array(
                                          z.object({
                                            from: z
                                              .number()
                                              .describe(
                                                'Inclusive lower bound of the range.'
                                              )
                                              .optional()
                                              .nullable(),
                                            to: z
                                              .number()
                                              .describe(
                                                'Exclusive upper bound of the range.'
                                              )
                                              .optional()
                                              .nullable(),
                                            count: z
                                              .number()
                                              .int()
                                              .describe(
                                                'Count of entities in this range.'
                                              )
                                              .optional(),
                                          })
                                        )
                                        .max(50)
                                        .optional(),
                                    })
                                    .describe('Range aggregation results.'),
                                }),
                                z.object({
                                  values: z.never().optional(),
                                  ranges: z.never().optional(),
                                  scalar: z
                                    .object({
                                      type: z
                                        .enum(['COUNT_DISTINCT', 'MIN', 'MAX'])
                                        .describe('Type of scalar aggregation.')
                                        .optional(),
                                      value: z
                                        .number()
                                        .describe(
                                          'Value of the scalar aggregation.'
                                        )
                                        .optional(),
                                    })
                                    .describe('Scalar aggregation results.'),
                                }),
                              ])
                            )
                            .describe('Nested aggregations.')
                            .optional(),
                        })
                      )
                      .max(1000)
                      .optional(),
                  })
                  .describe('Group by value aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                nested: z.never().optional(),
                dateHistogram: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          value: z
                            .string()
                            .describe('Date in ISO 8601 format.')
                            .max(100)
                            .optional(),
                          count: z
                            .number()
                            .int()
                            .describe('Count of documents in the bucket.')
                            .optional(),
                        })
                      )
                      .max(200)
                      .optional(),
                  })
                  .describe('Date histogram aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          results: z
                            .record(
                              z.string(),
                              z.intersection(
                                z.object({}),
                                z.xor([
                                  z.object({
                                    value: z.never().optional(),
                                    range: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                  }),
                                  z.object({
                                    range: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                    value: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe('Value of the field.')
                                          .max(1000)
                                          .optional(),
                                        count: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Count of entities with this value.'
                                          )
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe('Value aggregation result.'),
                                  }),
                                  z.object({
                                    value: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                    range: z
                                      .object({
                                        from: z
                                          .number()
                                          .describe(
                                            'Inclusive lower bound of the range.'
                                          )
                                          .optional()
                                          .nullable(),
                                        to: z
                                          .number()
                                          .describe(
                                            'Exclusive upper bound of the range.'
                                          )
                                          .optional()
                                          .nullable(),
                                        count: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Count of entities in this range.'
                                          )
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe('Range aggregation result.'),
                                  }),
                                  z.object({
                                    value: z.never().optional(),
                                    range: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                    scalar: z
                                      .object({
                                        value: z
                                          .number()
                                          .describe(
                                            'Value of the scalar aggregation.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Scalar aggregation result.'),
                                  }),
                                  z.object({
                                    value: z.never().optional(),
                                    range: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe('Value of the field.')
                                          .max(1000)
                                          .optional(),
                                        count: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Count of entities with this value.'
                                          )
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe(
                                        'Date histogram aggregation result.'
                                      ),
                                  }),
                                ])
                              )
                            )
                            .describe('List of nested aggregations.')
                            .optional(),
                        })
                      )
                      .max(1000)
                      .optional(),
                  })
                  .describe('Nested aggregation results.'),
              }),
            ])
          )
        )
        .max(10000)
        .optional(),
    })
    .describe('Aggregation data')
    .optional(),
});
export const BulkCreateJoinApplicationsRequest = /*#__PURE__*/ z.object({
  joinApplications: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('JoinApplication ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time a supported lifecycle, payment, or tag action changes the JoinApplication.\n\nThis API does not provide a generic update operation. The revision is read-only and is not sent when creating or changing a JoinApplication.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the join application was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the join application was last updated.')
          .optional()
          .nullable(),
        memberId: z
          .string()
          .describe(
            "Member ID of the site member applying or invited to participate in the program.\n\nRequired when creating applications as a site owner with `manage_applications` permission.\nAutomatically set to the caller's member ID when members create their own applications."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          ),
        programId: z
          .string()
          .describe('Program ID which this join application is related to.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          ),
        status: z.enum([
          'INVITED',
          'APPROVAL_PENDING',
          'APPROVED',
          'CANCELLED',
          'DECLINED',
          'SUSPENDED',
        ]),
        paymentStatus: z
          .enum([
            'NO_PAYMENT_REQUIRED',
            'PAYMENT_PENDING',
            'PAYMENT_IN_PROGRESS',
            'PENDING_OWNER_APPROVAL',
            'PAYMENT_SUCCESSFUL',
            'PAYMENT_FAILED',
            'PAYMENT_CANCELED',
          ])
          .optional(),
        paymentInfo: z
          .object({
            paymentOrderId: z
              .string()
              .describe(
                'One-time payment fields (payment_order_id, offline_transaction_id, and coupon_id) can be populated together as applicable. They are mutually exclusive with paid_plan_ids, which is populated only for Pricing Plans payments.\nWix Payments order id, populated when UoU selects One-time payment at the checkout'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            offlineTransactionId: z
              .string()
              .describe(
                'Offline transaction id, populated when UoU selects manual payment type at the checkout'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            paidPlanIds: z.array(z.string()).max(100).optional(),
            couponId: z
              .string()
              .describe(
                'Coupon ID applied to the payment order, populated when a coupon is applied via ApplyCoupon'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
          })
          .describe(
            'Payment info - represents associated Wix Payments or Pricing Plans data.'
          )
          .optional(),
        member: z
          .object({
            firstName: z
              .string()
              .describe(
                "Read-only snapshot of the member identified by member_id. It is copied from the Members API and is not the authoritative member record.\nMember contact's first name"
              )
              .max(1000)
              .optional()
              .nullable(),
            lastName: z
              .string()
              .describe("Member contact's last name")
              .max(1000)
              .optional()
              .nullable(),
            email: z
              .string()
              .describe('Member login email')
              .email()
              .optional()
              .nullable(),
            contactId: z
              .string()
              .describe('Member contact id')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            nickname: z
              .string()
              .describe('Member nickname')
              .max(1000)
              .optional()
              .nullable(),
            profileImageUrl: z
              .string()
              .describe('Member profile image url')
              .url()
              .optional()
              .nullable(),
          })
          .describe(
            'Read-only snapshot of member information from the Members API for `member_id`.\nThis is not the authoritative member record.'
          )
          .optional(),
        participantId: z
          .string()
          .describe(
            'Resulting participant (on APPROVED & NO_PAYMENT_REQUIRED/PAYMENT_SUCCESSFUL).'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for the JoinApplication.\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields)\nmust be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        tags: z
          .object({
            privateTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that require an additional permission in order to access them, typically restricted from site members and visitors.'
              )
              .optional(),
            publicTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone with access to the entity, including site members and visitors.'
              )
              .optional(),
          })
          .describe('Tags for categorizing and organizing join applications.')
          .optional(),
      })
    )
    .min(1)
    .max(100),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe(
          'set to `true` if you wish to receive back the created JoinApplications in the response'
        )
        .optional(),
    })
    .optional(),
});
export const BulkCreateJoinApplicationsResponse = /*#__PURE__*/ z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Provided only whenever possible. For example, `itemId` can't be provided when item creation has failed."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .min(0)
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action for this item was successful. When `false`, the `error` field is returned.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata regarding the specific single create operation')
          .optional(),
        item: z
          .object({
            _id: z
              .string()
              .describe('JoinApplication ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time a supported lifecycle, payment, or tag action changes the JoinApplication.\n\nThis API does not provide a generic update operation. The revision is read-only and is not sent when creating or changing a JoinApplication.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe('Date and time the join application was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the join application was last updated.')
              .optional()
              .nullable(),
            memberId: z
              .string()
              .describe(
                "Member ID of the site member applying or invited to participate in the program.\n\nRequired when creating applications as a site owner with `manage_applications` permission.\nAutomatically set to the caller's member ID when members create their own applications."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            programId: z
              .string()
              .describe('Program ID which this join application is related to.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            status: z
              .enum([
                'INVITED',
                'APPROVAL_PENDING',
                'APPROVED',
                'CANCELLED',
                'DECLINED',
                'SUSPENDED',
              ])
              .describe(
                'Current status of the join application.\n\nRead-only for member-initiated applications.\nSite owners with `manage_applications` permission can set an initial status when creating applications on behalf of members.'
              )
              .optional(),
            paymentStatus: z
              .enum([
                'NO_PAYMENT_REQUIRED',
                'PAYMENT_PENDING',
                'PAYMENT_IN_PROGRESS',
                'PENDING_OWNER_APPROVAL',
                'PAYMENT_SUCCESSFUL',
                'PAYMENT_FAILED',
                'PAYMENT_CANCELED',
              ])
              .describe('Payment status of join application.')
              .optional(),
            paymentInfo: z
              .object({
                paymentOrderId: z
                  .string()
                  .describe(
                    'One-time payment fields (payment_order_id, offline_transaction_id, and coupon_id) can be populated together as applicable. They are mutually exclusive with paid_plan_ids, which is populated only for Pricing Plans payments.\nWix Payments order id, populated when UoU selects One-time payment at the checkout'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                offlineTransactionId: z
                  .string()
                  .describe(
                    'Offline transaction id, populated when UoU selects manual payment type at the checkout'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                paidPlanIds: z.array(z.string()).max(100).optional(),
                couponId: z
                  .string()
                  .describe(
                    'Coupon ID applied to the payment order, populated when a coupon is applied via ApplyCoupon'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'Payment info - represents associated Wix Payments or Pricing Plans data.'
              )
              .optional(),
            member: z
              .object({
                firstName: z
                  .string()
                  .describe(
                    "Read-only snapshot of the member identified by member_id. It is copied from the Members API and is not the authoritative member record.\nMember contact's first name"
                  )
                  .max(1000)
                  .optional()
                  .nullable(),
                lastName: z
                  .string()
                  .describe("Member contact's last name")
                  .max(1000)
                  .optional()
                  .nullable(),
                email: z
                  .string()
                  .describe('Member login email')
                  .email()
                  .optional()
                  .nullable(),
                contactId: z
                  .string()
                  .describe('Member contact id')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                nickname: z
                  .string()
                  .describe('Member nickname')
                  .max(1000)
                  .optional()
                  .nullable(),
                profileImageUrl: z
                  .string()
                  .describe('Member profile image url')
                  .url()
                  .optional()
                  .nullable(),
              })
              .describe(
                'Read-only snapshot of member information from the Members API for `member_id`.\nThis is not the authoritative member record.'
              )
              .optional(),
            participantId: z
              .string()
              .describe(
                'Resulting participant (on APPROVED & NO_PAYMENT_REQUIRED/PAYMENT_SUCCESSFUL).'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe(
                'Custom field data for the JoinApplication.\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields)\nmust be configured in the app dashboard before they can be accessed with API calls.'
              )
              .optional(),
            tags: z
              .object({
                privateTags: z
                  .object({ tagIds: z.array(z.string()).max(100).optional() })
                  .describe(
                    'Tags that require an additional permission in order to access them, typically restricted from site members and visitors.'
                  )
                  .optional(),
                publicTags: z
                  .object({ tagIds: z.array(z.string()).max(100).optional() })
                  .describe(
                    'Tags that are exposed to anyone with access to the entity, including site members and visitors.'
                  )
                  .optional(),
              })
              .describe(
                'Tags for categorizing and organizing join applications.'
              )
              .optional(),
          })
          .describe(
            'Only exists if `returnEntity` was set to true in the request'
          )
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .min(0)
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .min(0)
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .min(0)
        .optional(),
    })
    .describe('Metadata regarding the bulk create operation')
    .optional(),
});
export const BulkDeleteJoinApplicationsRequest = /*#__PURE__*/ z.object({
  joinApplicationIds: z.array(z.string()).min(1).max(100),
});
export const BulkDeleteJoinApplicationsResponse = /*#__PURE__*/ z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Provided only whenever possible. For example, `itemId` can't be provided when item creation has failed."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .min(0)
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action for this item was successful. When `false`, the `error` field is returned.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata regarding the specific single delete operation')
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .min(0)
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .min(0)
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .min(0)
        .optional(),
    })
    .describe('Metadata regarding the bulk delete operation')
    .optional(),
});
export const GetCurrentJoinApplicationRequest = /*#__PURE__*/ z.object({
  programId: z
    .string()
    .describe("Program ID to look up the caller's join application for")
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetCurrentJoinApplicationResponse = /*#__PURE__*/ z.object({
  joinApplication: z
    .object({
      _id: z
        .string()
        .describe('JoinApplication ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time a supported lifecycle, payment, or tag action changes the JoinApplication.\n\nThis API does not provide a generic update operation. The revision is read-only and is not sent when creating or changing a JoinApplication.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the join application was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the join application was last updated.')
        .optional()
        .nullable(),
      memberId: z
        .string()
        .describe(
          "Member ID of the site member applying or invited to participate in the program.\n\nRequired when creating applications as a site owner with `manage_applications` permission.\nAutomatically set to the caller's member ID when members create their own applications."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      programId: z
        .string()
        .describe('Program ID which this join application is related to.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      status: z
        .enum([
          'INVITED',
          'APPROVAL_PENDING',
          'APPROVED',
          'CANCELLED',
          'DECLINED',
          'SUSPENDED',
        ])
        .describe(
          'Current status of the join application.\n\nRead-only for member-initiated applications.\nSite owners with `manage_applications` permission can set an initial status when creating applications on behalf of members.'
        )
        .optional(),
      paymentStatus: z
        .enum([
          'NO_PAYMENT_REQUIRED',
          'PAYMENT_PENDING',
          'PAYMENT_IN_PROGRESS',
          'PENDING_OWNER_APPROVAL',
          'PAYMENT_SUCCESSFUL',
          'PAYMENT_FAILED',
          'PAYMENT_CANCELED',
        ])
        .describe('Payment status of join application.')
        .optional(),
      paymentInfo: z
        .object({
          paymentOrderId: z
            .string()
            .describe(
              'One-time payment fields (payment_order_id, offline_transaction_id, and coupon_id) can be populated together as applicable. They are mutually exclusive with paid_plan_ids, which is populated only for Pricing Plans payments.\nWix Payments order id, populated when UoU selects One-time payment at the checkout'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          offlineTransactionId: z
            .string()
            .describe(
              'Offline transaction id, populated when UoU selects manual payment type at the checkout'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          paidPlanIds: z.array(z.string()).max(100).optional(),
          couponId: z
            .string()
            .describe(
              'Coupon ID applied to the payment order, populated when a coupon is applied via ApplyCoupon'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Payment info - represents associated Wix Payments or Pricing Plans data.'
        )
        .optional(),
      member: z
        .object({
          firstName: z
            .string()
            .describe(
              "Read-only snapshot of the member identified by member_id. It is copied from the Members API and is not the authoritative member record.\nMember contact's first name"
            )
            .max(1000)
            .optional()
            .nullable(),
          lastName: z
            .string()
            .describe("Member contact's last name")
            .max(1000)
            .optional()
            .nullable(),
          email: z
            .string()
            .describe('Member login email')
            .email()
            .optional()
            .nullable(),
          contactId: z
            .string()
            .describe('Member contact id')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          nickname: z
            .string()
            .describe('Member nickname')
            .max(1000)
            .optional()
            .nullable(),
          profileImageUrl: z
            .string()
            .describe('Member profile image url')
            .url()
            .optional()
            .nullable(),
        })
        .describe(
          'Read-only snapshot of member information from the Members API for `member_id`.\nThis is not the authoritative member record.'
        )
        .optional(),
      participantId: z
        .string()
        .describe(
          'Resulting participant (on APPROVED & NO_PAYMENT_REQUIRED/PAYMENT_SUCCESSFUL).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the JoinApplication.\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields)\nmust be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, typically restricted from site members and visitors.'
            )
            .optional(),
          publicTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone with access to the entity, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags for categorizing and organizing join applications.')
        .optional(),
    })
    .describe('The found JoinApplication (unique per member+program).')
    .optional(),
});
export const MyJoinApplicationRequest = /*#__PURE__*/ z.object({
  programId: z
    .string()
    .describe("Program ID to look up the caller's join application for")
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const MyJoinApplicationResponse = /*#__PURE__*/ z.object({
  joinApplication: z
    .object({
      _id: z
        .string()
        .describe('JoinApplication ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time a supported lifecycle, payment, or tag action changes the JoinApplication.\n\nThis API does not provide a generic update operation. The revision is read-only and is not sent when creating or changing a JoinApplication.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the join application was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the join application was last updated.')
        .optional()
        .nullable(),
      memberId: z
        .string()
        .describe(
          "Member ID of the site member applying or invited to participate in the program.\n\nRequired when creating applications as a site owner with `manage_applications` permission.\nAutomatically set to the caller's member ID when members create their own applications."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      programId: z
        .string()
        .describe('Program ID which this join application is related to.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      status: z
        .enum([
          'INVITED',
          'APPROVAL_PENDING',
          'APPROVED',
          'CANCELLED',
          'DECLINED',
          'SUSPENDED',
        ])
        .describe(
          'Current status of the join application.\n\nRead-only for member-initiated applications.\nSite owners with `manage_applications` permission can set an initial status when creating applications on behalf of members.'
        )
        .optional(),
      paymentStatus: z
        .enum([
          'NO_PAYMENT_REQUIRED',
          'PAYMENT_PENDING',
          'PAYMENT_IN_PROGRESS',
          'PENDING_OWNER_APPROVAL',
          'PAYMENT_SUCCESSFUL',
          'PAYMENT_FAILED',
          'PAYMENT_CANCELED',
        ])
        .describe('Payment status of join application.')
        .optional(),
      paymentInfo: z
        .object({
          paymentOrderId: z
            .string()
            .describe(
              'One-time payment fields (payment_order_id, offline_transaction_id, and coupon_id) can be populated together as applicable. They are mutually exclusive with paid_plan_ids, which is populated only for Pricing Plans payments.\nWix Payments order id, populated when UoU selects One-time payment at the checkout'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          offlineTransactionId: z
            .string()
            .describe(
              'Offline transaction id, populated when UoU selects manual payment type at the checkout'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          paidPlanIds: z.array(z.string()).max(100).optional(),
          couponId: z
            .string()
            .describe(
              'Coupon ID applied to the payment order, populated when a coupon is applied via ApplyCoupon'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Payment info - represents associated Wix Payments or Pricing Plans data.'
        )
        .optional(),
      member: z
        .object({
          firstName: z
            .string()
            .describe(
              "Read-only snapshot of the member identified by member_id. It is copied from the Members API and is not the authoritative member record.\nMember contact's first name"
            )
            .max(1000)
            .optional()
            .nullable(),
          lastName: z
            .string()
            .describe("Member contact's last name")
            .max(1000)
            .optional()
            .nullable(),
          email: z
            .string()
            .describe('Member login email')
            .email()
            .optional()
            .nullable(),
          contactId: z
            .string()
            .describe('Member contact id')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          nickname: z
            .string()
            .describe('Member nickname')
            .max(1000)
            .optional()
            .nullable(),
          profileImageUrl: z
            .string()
            .describe('Member profile image url')
            .url()
            .optional()
            .nullable(),
        })
        .describe(
          'Read-only snapshot of member information from the Members API for `member_id`.\nThis is not the authoritative member record.'
        )
        .optional(),
      participantId: z
        .string()
        .describe(
          'Resulting participant (on APPROVED & NO_PAYMENT_REQUIRED/PAYMENT_SUCCESSFUL).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the JoinApplication.\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields)\nmust be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, typically restricted from site members and visitors.'
            )
            .optional(),
          publicTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone with access to the entity, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags for categorizing and organizing join applications.')
        .optional(),
    })
    .describe('The found JoinApplication (unique per member+program).')
    .optional(),
});
export const ApproveJoinApplicationRequest = /*#__PURE__*/ z.object({
  joinApplicationId: z
    .string()
    .describe('ID of the JoinApplication to approve')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const ApproveJoinApplicationResponse = /*#__PURE__*/ z.object({
  joinApplication: z
    .object({
      _id: z
        .string()
        .describe('JoinApplication ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time a supported lifecycle, payment, or tag action changes the JoinApplication.\n\nThis API does not provide a generic update operation. The revision is read-only and is not sent when creating or changing a JoinApplication.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the join application was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the join application was last updated.')
        .optional()
        .nullable(),
      memberId: z
        .string()
        .describe(
          "Member ID of the site member applying or invited to participate in the program.\n\nRequired when creating applications as a site owner with `manage_applications` permission.\nAutomatically set to the caller's member ID when members create their own applications."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      programId: z
        .string()
        .describe('Program ID which this join application is related to.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      status: z
        .enum([
          'INVITED',
          'APPROVAL_PENDING',
          'APPROVED',
          'CANCELLED',
          'DECLINED',
          'SUSPENDED',
        ])
        .describe(
          'Current status of the join application.\n\nRead-only for member-initiated applications.\nSite owners with `manage_applications` permission can set an initial status when creating applications on behalf of members.'
        )
        .optional(),
      paymentStatus: z
        .enum([
          'NO_PAYMENT_REQUIRED',
          'PAYMENT_PENDING',
          'PAYMENT_IN_PROGRESS',
          'PENDING_OWNER_APPROVAL',
          'PAYMENT_SUCCESSFUL',
          'PAYMENT_FAILED',
          'PAYMENT_CANCELED',
        ])
        .describe('Payment status of join application.')
        .optional(),
      paymentInfo: z
        .object({
          paymentOrderId: z
            .string()
            .describe(
              'One-time payment fields (payment_order_id, offline_transaction_id, and coupon_id) can be populated together as applicable. They are mutually exclusive with paid_plan_ids, which is populated only for Pricing Plans payments.\nWix Payments order id, populated when UoU selects One-time payment at the checkout'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          offlineTransactionId: z
            .string()
            .describe(
              'Offline transaction id, populated when UoU selects manual payment type at the checkout'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          paidPlanIds: z.array(z.string()).max(100).optional(),
          couponId: z
            .string()
            .describe(
              'Coupon ID applied to the payment order, populated when a coupon is applied via ApplyCoupon'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Payment info - represents associated Wix Payments or Pricing Plans data.'
        )
        .optional(),
      member: z
        .object({
          firstName: z
            .string()
            .describe(
              "Read-only snapshot of the member identified by member_id. It is copied from the Members API and is not the authoritative member record.\nMember contact's first name"
            )
            .max(1000)
            .optional()
            .nullable(),
          lastName: z
            .string()
            .describe("Member contact's last name")
            .max(1000)
            .optional()
            .nullable(),
          email: z
            .string()
            .describe('Member login email')
            .email()
            .optional()
            .nullable(),
          contactId: z
            .string()
            .describe('Member contact id')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          nickname: z
            .string()
            .describe('Member nickname')
            .max(1000)
            .optional()
            .nullable(),
          profileImageUrl: z
            .string()
            .describe('Member profile image url')
            .url()
            .optional()
            .nullable(),
        })
        .describe(
          'Read-only snapshot of member information from the Members API for `member_id`.\nThis is not the authoritative member record.'
        )
        .optional(),
      participantId: z
        .string()
        .describe(
          'Resulting participant (on APPROVED & NO_PAYMENT_REQUIRED/PAYMENT_SUCCESSFUL).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the JoinApplication.\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields)\nmust be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, typically restricted from site members and visitors.'
            )
            .optional(),
          publicTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone with access to the entity, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags for categorizing and organizing join applications.')
        .optional(),
    })
    .describe('The approved JoinApplication with updated status')
    .optional(),
});
export const DeclineJoinApplicationRequest = /*#__PURE__*/ z.object({
  joinApplicationId: z
    .string()
    .describe('ID of the JoinApplication to decline')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeclineJoinApplicationResponse = /*#__PURE__*/ z.object({
  joinApplication: z
    .object({
      _id: z
        .string()
        .describe('JoinApplication ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time a supported lifecycle, payment, or tag action changes the JoinApplication.\n\nThis API does not provide a generic update operation. The revision is read-only and is not sent when creating or changing a JoinApplication.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the join application was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the join application was last updated.')
        .optional()
        .nullable(),
      memberId: z
        .string()
        .describe(
          "Member ID of the site member applying or invited to participate in the program.\n\nRequired when creating applications as a site owner with `manage_applications` permission.\nAutomatically set to the caller's member ID when members create their own applications."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      programId: z
        .string()
        .describe('Program ID which this join application is related to.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      status: z
        .enum([
          'INVITED',
          'APPROVAL_PENDING',
          'APPROVED',
          'CANCELLED',
          'DECLINED',
          'SUSPENDED',
        ])
        .describe(
          'Current status of the join application.\n\nRead-only for member-initiated applications.\nSite owners with `manage_applications` permission can set an initial status when creating applications on behalf of members.'
        )
        .optional(),
      paymentStatus: z
        .enum([
          'NO_PAYMENT_REQUIRED',
          'PAYMENT_PENDING',
          'PAYMENT_IN_PROGRESS',
          'PENDING_OWNER_APPROVAL',
          'PAYMENT_SUCCESSFUL',
          'PAYMENT_FAILED',
          'PAYMENT_CANCELED',
        ])
        .describe('Payment status of join application.')
        .optional(),
      paymentInfo: z
        .object({
          paymentOrderId: z
            .string()
            .describe(
              'One-time payment fields (payment_order_id, offline_transaction_id, and coupon_id) can be populated together as applicable. They are mutually exclusive with paid_plan_ids, which is populated only for Pricing Plans payments.\nWix Payments order id, populated when UoU selects One-time payment at the checkout'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          offlineTransactionId: z
            .string()
            .describe(
              'Offline transaction id, populated when UoU selects manual payment type at the checkout'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          paidPlanIds: z.array(z.string()).max(100).optional(),
          couponId: z
            .string()
            .describe(
              'Coupon ID applied to the payment order, populated when a coupon is applied via ApplyCoupon'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Payment info - represents associated Wix Payments or Pricing Plans data.'
        )
        .optional(),
      member: z
        .object({
          firstName: z
            .string()
            .describe(
              "Read-only snapshot of the member identified by member_id. It is copied from the Members API and is not the authoritative member record.\nMember contact's first name"
            )
            .max(1000)
            .optional()
            .nullable(),
          lastName: z
            .string()
            .describe("Member contact's last name")
            .max(1000)
            .optional()
            .nullable(),
          email: z
            .string()
            .describe('Member login email')
            .email()
            .optional()
            .nullable(),
          contactId: z
            .string()
            .describe('Member contact id')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          nickname: z
            .string()
            .describe('Member nickname')
            .max(1000)
            .optional()
            .nullable(),
          profileImageUrl: z
            .string()
            .describe('Member profile image url')
            .url()
            .optional()
            .nullable(),
        })
        .describe(
          'Read-only snapshot of member information from the Members API for `member_id`.\nThis is not the authoritative member record.'
        )
        .optional(),
      participantId: z
        .string()
        .describe(
          'Resulting participant (on APPROVED & NO_PAYMENT_REQUIRED/PAYMENT_SUCCESSFUL).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the JoinApplication.\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields)\nmust be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, typically restricted from site members and visitors.'
            )
            .optional(),
          publicTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone with access to the entity, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags for categorizing and organizing join applications.')
        .optional(),
    })
    .describe('The declined JoinApplication with updated status')
    .optional(),
});
export const CancelJoinApplicationRequest = /*#__PURE__*/ z.object({
  joinApplicationId: z
    .string()
    .describe('ID of the JoinApplication to cancel')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const CancelJoinApplicationResponse = /*#__PURE__*/ z.object({
  joinApplication: z
    .object({
      _id: z
        .string()
        .describe('JoinApplication ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time a supported lifecycle, payment, or tag action changes the JoinApplication.\n\nThis API does not provide a generic update operation. The revision is read-only and is not sent when creating or changing a JoinApplication.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the join application was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the join application was last updated.')
        .optional()
        .nullable(),
      memberId: z
        .string()
        .describe(
          "Member ID of the site member applying or invited to participate in the program.\n\nRequired when creating applications as a site owner with `manage_applications` permission.\nAutomatically set to the caller's member ID when members create their own applications."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      programId: z
        .string()
        .describe('Program ID which this join application is related to.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      status: z
        .enum([
          'INVITED',
          'APPROVAL_PENDING',
          'APPROVED',
          'CANCELLED',
          'DECLINED',
          'SUSPENDED',
        ])
        .describe(
          'Current status of the join application.\n\nRead-only for member-initiated applications.\nSite owners with `manage_applications` permission can set an initial status when creating applications on behalf of members.'
        )
        .optional(),
      paymentStatus: z
        .enum([
          'NO_PAYMENT_REQUIRED',
          'PAYMENT_PENDING',
          'PAYMENT_IN_PROGRESS',
          'PENDING_OWNER_APPROVAL',
          'PAYMENT_SUCCESSFUL',
          'PAYMENT_FAILED',
          'PAYMENT_CANCELED',
        ])
        .describe('Payment status of join application.')
        .optional(),
      paymentInfo: z
        .object({
          paymentOrderId: z
            .string()
            .describe(
              'One-time payment fields (payment_order_id, offline_transaction_id, and coupon_id) can be populated together as applicable. They are mutually exclusive with paid_plan_ids, which is populated only for Pricing Plans payments.\nWix Payments order id, populated when UoU selects One-time payment at the checkout'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          offlineTransactionId: z
            .string()
            .describe(
              'Offline transaction id, populated when UoU selects manual payment type at the checkout'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          paidPlanIds: z.array(z.string()).max(100).optional(),
          couponId: z
            .string()
            .describe(
              'Coupon ID applied to the payment order, populated when a coupon is applied via ApplyCoupon'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Payment info - represents associated Wix Payments or Pricing Plans data.'
        )
        .optional(),
      member: z
        .object({
          firstName: z
            .string()
            .describe(
              "Read-only snapshot of the member identified by member_id. It is copied from the Members API and is not the authoritative member record.\nMember contact's first name"
            )
            .max(1000)
            .optional()
            .nullable(),
          lastName: z
            .string()
            .describe("Member contact's last name")
            .max(1000)
            .optional()
            .nullable(),
          email: z
            .string()
            .describe('Member login email')
            .email()
            .optional()
            .nullable(),
          contactId: z
            .string()
            .describe('Member contact id')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          nickname: z
            .string()
            .describe('Member nickname')
            .max(1000)
            .optional()
            .nullable(),
          profileImageUrl: z
            .string()
            .describe('Member profile image url')
            .url()
            .optional()
            .nullable(),
        })
        .describe(
          'Read-only snapshot of member information from the Members API for `member_id`.\nThis is not the authoritative member record.'
        )
        .optional(),
      participantId: z
        .string()
        .describe(
          'Resulting participant (on APPROVED & NO_PAYMENT_REQUIRED/PAYMENT_SUCCESSFUL).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the JoinApplication.\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields)\nmust be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, typically restricted from site members and visitors.'
            )
            .optional(),
          publicTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone with access to the entity, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags for categorizing and organizing join applications.')
        .optional(),
    })
    .describe('The cancelled JoinApplication with updated status')
    .optional(),
});
export const AcceptInviteRequest = /*#__PURE__*/ z.object({
  joinApplicationId: z
    .string()
    .describe('ID of the JoinApplication to accept')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const AcceptInviteResponse = /*#__PURE__*/ z.object({
  joinApplication: z
    .object({
      _id: z
        .string()
        .describe('JoinApplication ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time a supported lifecycle, payment, or tag action changes the JoinApplication.\n\nThis API does not provide a generic update operation. The revision is read-only and is not sent when creating or changing a JoinApplication.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the join application was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the join application was last updated.')
        .optional()
        .nullable(),
      memberId: z
        .string()
        .describe(
          "Member ID of the site member applying or invited to participate in the program.\n\nRequired when creating applications as a site owner with `manage_applications` permission.\nAutomatically set to the caller's member ID when members create their own applications."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      programId: z
        .string()
        .describe('Program ID which this join application is related to.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      status: z
        .enum([
          'INVITED',
          'APPROVAL_PENDING',
          'APPROVED',
          'CANCELLED',
          'DECLINED',
          'SUSPENDED',
        ])
        .describe(
          'Current status of the join application.\n\nRead-only for member-initiated applications.\nSite owners with `manage_applications` permission can set an initial status when creating applications on behalf of members.'
        )
        .optional(),
      paymentStatus: z
        .enum([
          'NO_PAYMENT_REQUIRED',
          'PAYMENT_PENDING',
          'PAYMENT_IN_PROGRESS',
          'PENDING_OWNER_APPROVAL',
          'PAYMENT_SUCCESSFUL',
          'PAYMENT_FAILED',
          'PAYMENT_CANCELED',
        ])
        .describe('Payment status of join application.')
        .optional(),
      paymentInfo: z
        .object({
          paymentOrderId: z
            .string()
            .describe(
              'One-time payment fields (payment_order_id, offline_transaction_id, and coupon_id) can be populated together as applicable. They are mutually exclusive with paid_plan_ids, which is populated only for Pricing Plans payments.\nWix Payments order id, populated when UoU selects One-time payment at the checkout'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          offlineTransactionId: z
            .string()
            .describe(
              'Offline transaction id, populated when UoU selects manual payment type at the checkout'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          paidPlanIds: z.array(z.string()).max(100).optional(),
          couponId: z
            .string()
            .describe(
              'Coupon ID applied to the payment order, populated when a coupon is applied via ApplyCoupon'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Payment info - represents associated Wix Payments or Pricing Plans data.'
        )
        .optional(),
      member: z
        .object({
          firstName: z
            .string()
            .describe(
              "Read-only snapshot of the member identified by member_id. It is copied from the Members API and is not the authoritative member record.\nMember contact's first name"
            )
            .max(1000)
            .optional()
            .nullable(),
          lastName: z
            .string()
            .describe("Member contact's last name")
            .max(1000)
            .optional()
            .nullable(),
          email: z
            .string()
            .describe('Member login email')
            .email()
            .optional()
            .nullable(),
          contactId: z
            .string()
            .describe('Member contact id')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          nickname: z
            .string()
            .describe('Member nickname')
            .max(1000)
            .optional()
            .nullable(),
          profileImageUrl: z
            .string()
            .describe('Member profile image url')
            .url()
            .optional()
            .nullable(),
        })
        .describe(
          'Read-only snapshot of member information from the Members API for `member_id`.\nThis is not the authoritative member record.'
        )
        .optional(),
      participantId: z
        .string()
        .describe(
          'Resulting participant (on APPROVED & NO_PAYMENT_REQUIRED/PAYMENT_SUCCESSFUL).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the JoinApplication.\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields)\nmust be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, typically restricted from site members and visitors.'
            )
            .optional(),
          publicTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone with access to the entity, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags for categorizing and organizing join applications.')
        .optional(),
    })
    .describe(
      'The JoinApplication with status updated from INVITED to APPROVED'
    )
    .optional(),
});
export const BulkUpdateJoinApplicationTagsRequest = /*#__PURE__*/ z.object({
  joinApplicationIds: z.array(z.string()).min(1).max(100),
  options: z
    .object({
      assignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, typically restricted from site members and visitors.'
            )
            .optional(),
          publicTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone with access to the entity, including site members and visitors.'
            )
            .optional(),
        })
        .describe(
          'Tags to assign to the JoinApplications. At least one of `assign_tags` or `unassign_tags` must be specified.'
        )
        .optional(),
      unassignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, typically restricted from site members and visitors.'
            )
            .optional(),
          publicTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone with access to the entity, including site members and visitors.'
            )
            .optional(),
        })
        .describe(
          'Tags to unassign from the JoinApplications. At least one of `assign_tags` or `unassign_tags` must be specified.'
        )
        .optional(),
    })
    .optional(),
});
export const BulkUpdateJoinApplicationTagsResponse = /*#__PURE__*/ z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Provided only whenever possible. For example, `itemId` can't be provided when item creation has failed."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .min(0)
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action for this item was successful. When `false`, the `error` field is returned.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata for this specific item')
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .min(0)
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .min(0)
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .min(0)
        .optional(),
    })
    .describe('Metadata about the bulk operation')
    .optional(),
});
export const BulkUpdateJoinApplicationTagsByFilterRequest =
  /*#__PURE__*/ z.object({
    filter: z
      .record(z.string(), z.any())
      .describe('WQL filter to select JoinApplications'),
    options: z
      .object({
        assignTags: z
          .object({
            privateTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that require an additional permission in order to access them, typically restricted from site members and visitors.'
              )
              .optional(),
            publicTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone with access to the entity, including site members and visitors.'
              )
              .optional(),
          })
          .describe(
            'Tags to assign to matching JoinApplications. At least one of `assign_tags` or `unassign_tags` must be specified.'
          )
          .optional(),
        unassignTags: z
          .object({
            privateTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that require an additional permission in order to access them, typically restricted from site members and visitors.'
              )
              .optional(),
            publicTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone with access to the entity, including site members and visitors.'
              )
              .optional(),
          })
          .describe(
            'Tags to unassign from matching JoinApplications. At least one of `assign_tags` or `unassign_tags` must be specified.'
          )
          .optional(),
      })
      .optional(),
  });
export const BulkUpdateJoinApplicationTagsByFilterResponse =
  /*#__PURE__*/ z.object({
    jobId: z
      .string()
      .describe(
        'Job ID for tracking the async operation. Pass this ID to Get Async Job to check operation status and results.'
      )
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional(),
  });
export const PreparePaymentRequest = /*#__PURE__*/ z.object({
  joinApplicationId: z
    .string()
    .describe('ID of the JoinApplication to prepare payment for')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  paymentType: z.enum(['SINGLE_PAYMENT', 'PAID_PLANS']),
});
export const PreparePaymentResponse = /*#__PURE__*/ z.object({
  joinApplication: z
    .object({
      _id: z
        .string()
        .describe('JoinApplication ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time a supported lifecycle, payment, or tag action changes the JoinApplication.\n\nThis API does not provide a generic update operation. The revision is read-only and is not sent when creating or changing a JoinApplication.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the join application was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the join application was last updated.')
        .optional()
        .nullable(),
      memberId: z
        .string()
        .describe(
          "Member ID of the site member applying or invited to participate in the program.\n\nRequired when creating applications as a site owner with `manage_applications` permission.\nAutomatically set to the caller's member ID when members create their own applications."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      programId: z
        .string()
        .describe('Program ID which this join application is related to.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      status: z
        .enum([
          'INVITED',
          'APPROVAL_PENDING',
          'APPROVED',
          'CANCELLED',
          'DECLINED',
          'SUSPENDED',
        ])
        .describe(
          'Current status of the join application.\n\nRead-only for member-initiated applications.\nSite owners with `manage_applications` permission can set an initial status when creating applications on behalf of members.'
        )
        .optional(),
      paymentStatus: z
        .enum([
          'NO_PAYMENT_REQUIRED',
          'PAYMENT_PENDING',
          'PAYMENT_IN_PROGRESS',
          'PENDING_OWNER_APPROVAL',
          'PAYMENT_SUCCESSFUL',
          'PAYMENT_FAILED',
          'PAYMENT_CANCELED',
        ])
        .describe('Payment status of join application.')
        .optional(),
      paymentInfo: z
        .object({
          paymentOrderId: z
            .string()
            .describe(
              'One-time payment fields (payment_order_id, offline_transaction_id, and coupon_id) can be populated together as applicable. They are mutually exclusive with paid_plan_ids, which is populated only for Pricing Plans payments.\nWix Payments order id, populated when UoU selects One-time payment at the checkout'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          offlineTransactionId: z
            .string()
            .describe(
              'Offline transaction id, populated when UoU selects manual payment type at the checkout'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          paidPlanIds: z.array(z.string()).max(100).optional(),
          couponId: z
            .string()
            .describe(
              'Coupon ID applied to the payment order, populated when a coupon is applied via ApplyCoupon'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Payment info - represents associated Wix Payments or Pricing Plans data.'
        )
        .optional(),
      member: z
        .object({
          firstName: z
            .string()
            .describe(
              "Read-only snapshot of the member identified by member_id. It is copied from the Members API and is not the authoritative member record.\nMember contact's first name"
            )
            .max(1000)
            .optional()
            .nullable(),
          lastName: z
            .string()
            .describe("Member contact's last name")
            .max(1000)
            .optional()
            .nullable(),
          email: z
            .string()
            .describe('Member login email')
            .email()
            .optional()
            .nullable(),
          contactId: z
            .string()
            .describe('Member contact id')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          nickname: z
            .string()
            .describe('Member nickname')
            .max(1000)
            .optional()
            .nullable(),
          profileImageUrl: z
            .string()
            .describe('Member profile image url')
            .url()
            .optional()
            .nullable(),
        })
        .describe(
          'Read-only snapshot of member information from the Members API for `member_id`.\nThis is not the authoritative member record.'
        )
        .optional(),
      participantId: z
        .string()
        .describe(
          'Resulting participant (on APPROVED & NO_PAYMENT_REQUIRED/PAYMENT_SUCCESSFUL).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the JoinApplication.\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields)\nmust be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, typically restricted from site members and visitors.'
            )
            .optional(),
          publicTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone with access to the entity, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags for categorizing and organizing join applications.')
        .optional(),
    })
    .describe(
      'JoinApplication with payment info refreshed and payment status set to PAYMENT_IN_PROGRESS.'
    )
    .optional(),
});
export const ApplyCouponRequest = /*#__PURE__*/ z.object({
  joinApplicationId: z
    .string()
    .describe('ID of the JoinApplication to apply the coupon to')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  couponCode: z.string().describe('Coupon code to apply').max(100),
});
export const ApplyCouponResponse = /*#__PURE__*/ z.object({
  couponId: z
    .string()
    .describe('Coupon ID that was applied')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  subTotal: z
    .string()
    .describe(
      'Original order subtotal before discount, formatted using the predefined precision of the site currency configured in Site Properties. The currency is not included in the value.'
    )
    .max(50)
    .optional(),
  discount: z
    .string()
    .describe(
      'Discount amount applied, formatted using the predefined precision of the site currency configured in Site Properties. The currency is not included in the value.'
    )
    .max(50)
    .optional(),
  total: z
    .string()
    .describe(
      'Final total after discount, formatted using the predefined precision of the site currency configured in Site Properties. The currency is not included in the value.'
    )
    .max(50)
    .optional(),
});
export const RemoveCouponRequest = /*#__PURE__*/ z.object({
  joinApplicationId: z
    .string()
    .describe('ID of the JoinApplication to remove the coupon from')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  couponId: z
    .string()
    .describe(
      'ID of the coupon to remove (must match the coupon applied to the order)'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const RemoveCouponResponse = /*#__PURE__*/ z.object({
  total: z
    .string()
    .describe(
      'Final total after coupon removal, formatted using the predefined precision of the site currency configured in Site Properties. The currency is not included in the value.'
    )
    .max(50)
    .optional(),
});
export const CompleteFreeCouponPaymentRequest = /*#__PURE__*/ z.object({
  joinApplicationId: z
    .string()
    .describe('ID of the JoinApplication to complete free coupon payment for')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const CompleteFreeCouponPaymentResponse = /*#__PURE__*/ z.object({
  joinApplication: z
    .object({
      _id: z
        .string()
        .describe('JoinApplication ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time a supported lifecycle, payment, or tag action changes the JoinApplication.\n\nThis API does not provide a generic update operation. The revision is read-only and is not sent when creating or changing a JoinApplication.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the join application was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the join application was last updated.')
        .optional()
        .nullable(),
      memberId: z
        .string()
        .describe(
          "Member ID of the site member applying or invited to participate in the program.\n\nRequired when creating applications as a site owner with `manage_applications` permission.\nAutomatically set to the caller's member ID when members create their own applications."
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      programId: z
        .string()
        .describe('Program ID which this join application is related to.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      status: z
        .enum([
          'INVITED',
          'APPROVAL_PENDING',
          'APPROVED',
          'CANCELLED',
          'DECLINED',
          'SUSPENDED',
        ])
        .describe(
          'Current status of the join application.\n\nRead-only for member-initiated applications.\nSite owners with `manage_applications` permission can set an initial status when creating applications on behalf of members.'
        )
        .optional(),
      paymentStatus: z
        .enum([
          'NO_PAYMENT_REQUIRED',
          'PAYMENT_PENDING',
          'PAYMENT_IN_PROGRESS',
          'PENDING_OWNER_APPROVAL',
          'PAYMENT_SUCCESSFUL',
          'PAYMENT_FAILED',
          'PAYMENT_CANCELED',
        ])
        .describe('Payment status of join application.')
        .optional(),
      paymentInfo: z
        .object({
          paymentOrderId: z
            .string()
            .describe(
              'One-time payment fields (payment_order_id, offline_transaction_id, and coupon_id) can be populated together as applicable. They are mutually exclusive with paid_plan_ids, which is populated only for Pricing Plans payments.\nWix Payments order id, populated when UoU selects One-time payment at the checkout'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          offlineTransactionId: z
            .string()
            .describe(
              'Offline transaction id, populated when UoU selects manual payment type at the checkout'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          paidPlanIds: z.array(z.string()).max(100).optional(),
          couponId: z
            .string()
            .describe(
              'Coupon ID applied to the payment order, populated when a coupon is applied via ApplyCoupon'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Payment info - represents associated Wix Payments or Pricing Plans data.'
        )
        .optional(),
      member: z
        .object({
          firstName: z
            .string()
            .describe(
              "Read-only snapshot of the member identified by member_id. It is copied from the Members API and is not the authoritative member record.\nMember contact's first name"
            )
            .max(1000)
            .optional()
            .nullable(),
          lastName: z
            .string()
            .describe("Member contact's last name")
            .max(1000)
            .optional()
            .nullable(),
          email: z
            .string()
            .describe('Member login email')
            .email()
            .optional()
            .nullable(),
          contactId: z
            .string()
            .describe('Member contact id')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          nickname: z
            .string()
            .describe('Member nickname')
            .max(1000)
            .optional()
            .nullable(),
          profileImageUrl: z
            .string()
            .describe('Member profile image url')
            .url()
            .optional()
            .nullable(),
        })
        .describe(
          'Read-only snapshot of member information from the Members API for `member_id`.\nThis is not the authoritative member record.'
        )
        .optional(),
      participantId: z
        .string()
        .describe(
          'Resulting participant (on APPROVED & NO_PAYMENT_REQUIRED/PAYMENT_SUCCESSFUL).'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the JoinApplication.\n[Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields)\nmust be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, typically restricted from site members and visitors.'
            )
            .optional(),
          publicTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone with access to the entity, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags for categorizing and organizing join applications.')
        .optional(),
    })
    .describe(
      'The JoinApplication with payment status updated to PAYMENT_SUCCESSFUL'
    )
    .optional(),
});
export const BulkInviteMembersToProgramByFilterRequest = /*#__PURE__*/ z.object(
  {
    programId: z
      .string()
      .describe('Program to which bulk invite members.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      ),
    options: z
      .object({
        excludeMemberIds: z.array(z.string()).min(0).max(100).optional(),
      })
      .optional(),
  }
);
export const BulkInviteMembersToProgramByFilterResponse =
  /*#__PURE__*/ z.object({
    jobId: z
      .string()
      .describe(
        'Job ID for tracking the async operation. Pass this ID to Get Async Job to check operation status and results.'
      )
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional(),
  });
